// The portal's router (spec/15 §2.1). Three groups of routes under the root:
//
//   - public, in the `PublicFrame`: the landing (`/`) and the venue directory (`/venues`,
//     `/venues/$slug`), which a signed-out visitor may browse;
//   - sign-in (`/sign-in`), sign-up (`/sign-up`) and the page a refused provider round trip lands on
//     (`/sign-in/error`), each on the auth layout;
//   - finishing a sign-in started at a venue's PC (`/station-sign-in`), and ending a session it started from the
//     link sent afterwards (`/station-sign-in/end`), on the same layout and open to a signed-out visitor;
//   - the venue directory and a venue's page, in the signed-in frame for a player and the public frame otherwise;
//   - forgot password (`/forgot-password`), also how a signed-in player sets or changes one;
//   - signed-in, in the `AppShell` behind a session gate: home, play, wallet, passes (and claiming a guest pass), orders
//     and a receipt, account.
//
// A later screen is one more child route and, if it belongs on the bar, one more item in `AppShell`'s
// nav. The route tree is made fresh so the tests can mount the app at any URL with a memory history.

import type { ApiClient } from '@clavian/api-client';
import type { QueryClient } from '@tanstack/react-query';
import {
  createMemoryHistory,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router';

import { queryKeys } from './api/queries';
import { SignInErrorPage } from './auth/SignInErrorPage';
import { SignInPage } from './auth/SignInPage';
import { SignUpPage } from './auth/SignUpPage';
import { WelcomePage } from './auth/WelcomePage';
import { ForgotPasswordPage } from './auth/ForgotPasswordPage';
import { AppShell, SiteShell } from './shell/AppShell';
import { StationLinkPage, validateStationLinkSearch } from './station-link/StationLinkPage';
import { EndSessionPage } from './station-sign-in/EndSessionPage';
import { isStationSignInProvider, StationSignInPage, type StationSignInProvider } from './station-sign-in/StationSignInPage';
import { NotFound } from './shell/feedback';
import { AccountPage } from './screens/AccountPage';
import { GuestPassPage } from './screens/GuestPassPage';
import { HomePage } from './screens/HomePage';
import { Landing } from './screens/Landing';
import { MovedPage } from './screens/MovedPage';
import { OrderPage, OrdersPage } from './screens/OrdersPage';
import { PassesPage } from './screens/PassesPage';
import { PaymentCompletePage } from './screens/PaymentCompletePage';
import { PlayPage } from './screens/PlayPage';
import { VenuePage } from './screens/VenuePage';
import { VenuesPage } from './screens/VenuesPage';
import { WalletPage } from './screens/WalletPage';

export interface RouterContext {
  readonly api: ApiClient;
  readonly queryClient: QueryClient;
}

/** Back to where the visitor was going — only somewhere in this portal: a path, never `//host`. */
const validateRedirect = (search: Record<string, unknown>): { redirect?: string } =>
  typeof search['redirect'] === 'string' && /^\/(?![/\\])/.test(search['redirect']) ? { redirect: search['redirect'] } : {};

/** A string query parameter, or nothing. */
const text = (search: Record<string, unknown>, key: string): string | undefined =>
  typeof search[key] === 'string' && search[key] !== '' ? search[key] : undefined;

/**
 * What a Google, Discord or Steam round trip comes back with (`social.tsx`): the api's `error` code on a refusal,
 * and the `provider` the portal named when it started. Read as plain words, never trusted as anything else.
 */
const validateSocialReturn = (search: Record<string, unknown>): { error?: string; provider?: string } => {
  const error = text(search, 'error');
  const provider = text(search, 'provider');
  return { ...(error === undefined ? {} : { error }), ...(provider === undefined ? {} : { provider }) };
};

const validateAuthSearch = (search: Record<string, unknown>): { redirect?: string; error?: string; provider?: string } => ({
  ...validateRedirect(search),
  ...validateSocialReturn(search),
});

/** The account page, back from linking a provider: `linked=<provider>`, or a refusal's `error` and `provider`. */
const validateAccountSearch = (search: Record<string, unknown>): { linked?: string; error?: string; provider?: string } => {
  const linked = text(search, 'linked');
  return { ...(linked === undefined ? {} : { linked }), ...validateSocialReturn(search) };
};

function createRouteTree() {
  const rootRoute = createRootRouteWithContext<RouterContext>()({
    component: Outlet,
    notFoundComponent: NotFound,
  });

  const signInRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/sign-in',
    validateSearch: validateAuthSearch,
    component: SignInPage,
  });
  const signUpRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/sign-up',
    validateSearch: validateAuthSearch,
    component: SignUpPage,
  });
  // The api's own landing for a refusal it cannot send anywhere else (spec/07 §3.1): `PLAYER_WEB_URL/sign-in/error`.
  const signInErrorRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/sign-in/error',
    validateSearch: (search: Record<string, unknown>): { error?: string } => {
      const error = text(search, 'error');
      return error === undefined ? {} : { error };
    },
    component: SignInErrorPage,
  });

  // Public: the secret in the fragment is the authority until the player signs in here. `via` is only a hint; `error`
  // and `provider` are what a Google, Discord or Steam round trip started here comes back with when it is refused.
  const stationSignInRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/station-sign-in',
    validateSearch: (search: Record<string, unknown>): { via?: StationSignInProvider; error?: string; provider?: string } => ({
      ...(isStationSignInProvider(search['via']) ? { via: search['via'] } : {}),
      ...validateSocialReturn(search),
    }),
    component: StationSignInPage,
  });

  // Public: linking Google, Discord or Steam from a PC's sign-in window during a session. The secret in the fragment is
  // the authority; nobody signs in here. `linked` and `error` are what the provider's round trip comes back with.
  const stationLinkRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/station-link',
    validateSearch: validateStationLinkSearch,
    component: StationLinkPage,
  });

  // Public: "Not you? End it". The secret in the fragment is the authority; nobody signs in to use it.
  const endStationSessionRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/station-sign-in/end',
    component: EndSessionPage,
  });

  // The public pages sit directly under the root and wrap themselves in the PublicFrame; a signed-out
  // visitor may browse them.
  const landingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    beforeLoad: async ({ context }) => {
      // A signed-in visitor opens on Home rather than the marketing landing.
      const session = await context.queryClient
        .ensureQueryData({ queryKey: queryKeys.session, queryFn: () => context.api.getSession() })
        .catch(() => null);
      if (session != null) throw redirect({ to: '/home' });
    },
    component: Landing,
  });

  // Forgot password, and setting or changing one from Account: public; `email` prefills, `back` is where a signed-in
  // player returns (a path in this portal only).
  const forgotRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/forgot-password',
    validateSearch: (search: Record<string, unknown>): { email?: string; back?: string } => {
      const email = text(search, 'email');
      const back = text(search, 'back');
      return { ...(email === undefined ? {} : { email }), ...(back !== undefined && /^\/(?![/\\])/.test(back) ? { back } : {}) };
    },
    component: ForgotPasswordPage,
  });

  // The venue pages: the signed-in frame for a player, the public frame for a visitor (`SiteShell`).
  const siteLayout = createRoute({ getParentRoute: () => rootRoute, id: 'site', component: SiteShell });
  const venuesRoute = createRoute({ getParentRoute: () => siteLayout, path: '/venues', component: VenuesPage });
  const venueRoute = createRoute({ getParentRoute: () => siteLayout, path: '/venues/$slug', component: VenuePage });
  // The link in the "account moved" email (F-IAM-13): public, so a signed-out player sees what waits before signing in.
  const movedRoute = createRoute({ getParentRoute: () => rootRoute, path: '/moved', component: MovedPage });
  // Where the payment provider sends the player back after paying: public, it carries nothing to rely on (the page reads
  // what was remembered in this tab).
  const paymentCompleteRoute = createRoute({ getParentRoute: () => rootRoute, path: '/payment-complete', component: PaymentCompletePage });
  // Old links: buying moved to each venue's page (paid at the counter), and rewards are not offered.
  const buyRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/buy',
    beforeLoad: () => {
      throw redirect({ to: '/venues' });
    },
  });
  const rewardsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/rewards',
    beforeLoad: () => {
      throw redirect({ to: '/home' });
    },
  });

  // The signed-in frame: one pathless layout (the AppShell) behind a session gate. A signed-out
  // visitor is sent to sign in and brought back. The layout is transparent to the URL — its screens
  // are `/home`, `/buy`, … — while its route ids carry the `app` prefix (used by `from` below).
  const appLayout = createRoute({
    getParentRoute: () => rootRoute,
    id: 'app',
    beforeLoad: async ({ context, location }) => {
      const session = await context.queryClient
        .ensureQueryData({ queryKey: queryKeys.session, queryFn: () => context.api.getSession() })
        .catch(() => null);
      if (session == null) throw redirect({ to: '/sign-in', search: { redirect: location.href } });
      // A new account from Google, Discord or Steam completes its profile before anything else (spec/07 §3.2). An
      // answer that cannot be read lets the player through rather than locking them out.
      const me = await context.queryClient
        .ensureQueryData({ queryKey: queryKeys.me, queryFn: () => context.api.getMe() })
        .catch(() => null);
      if (me?.profileComplete === false) throw redirect({ to: '/welcome', search: { redirect: location.href } });
    },
    component: AppShell,
  });

  // "Complete your profile" (spec/07 §3.2): signed in, on the auth layout, outside the shell it guards.
  const welcomeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/welcome',
    validateSearch: validateRedirect,
    beforeLoad: async ({ context, location }) => {
      const session = await context.queryClient
        .ensureQueryData({ queryKey: queryKeys.session, queryFn: () => context.api.getSession() })
        .catch(() => null);
      if (session == null) throw redirect({ to: '/sign-in', search: { redirect: location.href } });
    },
    component: WelcomePage,
  });

  const homeRoute = createRoute({ getParentRoute: () => appLayout, path: '/home', component: HomePage });
  const playRoute = createRoute({ getParentRoute: () => appLayout, path: '/play', component: PlayPage });
  const walletRoute = createRoute({
    getParentRoute: () => appLayout,
    path: '/wallet',
    validateSearch: (search: Record<string, unknown>): { organization?: string } => {
      const organization = text(search, 'organization');
      return organization === undefined ? {} : { organization };
    },
    component: WalletPage,
  });
  const passesRoute = createRoute({ getParentRoute: () => appLayout, path: '/passes', component: PassesPage });
  // Claim a guest pass (F-SES-07): the ticket code from a counter receipt, `?code=` filling it in. Signed in; a signed-out
  // visitor is sent to sign in and back to this URL, code and all.
  const guestPassRoute = createRoute({
    getParentRoute: () => appLayout,
    path: '/guest-pass',
    validateSearch: (search: Record<string, unknown>): { code?: string } => {
      const code = text(search, 'code');
      // A code is at most a few characters; anything longer is not one, and is not echoed back into the page.
      return code === undefined || code.length > 16 ? {} : { code };
    },
    component: GuestPassPage,
  });
  const ordersRoute = createRoute({ getParentRoute: () => appLayout, path: '/orders', component: OrdersPage });
  const orderRoute = createRoute({ getParentRoute: () => appLayout, path: '/orders/$saleId', component: OrderPage });
  const accountRoute = createRoute({ getParentRoute: () => appLayout, path: '/account', validateSearch: validateAccountSearch, component: AccountPage });

  return rootRoute.addChildren([
    signInRoute,
    signUpRoute,
    signInErrorRoute,
    welcomeRoute,
    stationSignInRoute,
    endStationSessionRoute,
    stationLinkRoute,
    forgotRoute,
    landingRoute,
    siteLayout.addChildren([venuesRoute, venueRoute]),
    movedRoute,
    paymentCompleteRoute,
    buyRoute,
    rewardsRoute,
    appLayout.addChildren([homeRoute, playRoute, walletRoute, passesRoute, guestPassRoute, ordersRoute, orderRoute, accountRoute]),
  ]);
}

export type PlayerRouteTree = ReturnType<typeof createRouteTree>;

export function createPlayerRouter(context: RouterContext, history?: RouterHistory) {
  return createRouter({
    routeTree: createRouteTree(),
    context,
    defaultPreload: 'intent',
    // A query's own cache decides freshness; the router never serves a stale loader result.
    defaultPreloadStaleTime: 0,
    defaultNotFoundComponent: NotFound,
    ...(history === undefined ? {} : { history }),
  });
}

/** For tests: the portal at one URL, with no browser history. */
export const memoryHistory = (url: string): RouterHistory => createMemoryHistory({ initialEntries: [url] });

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createRouter<PlayerRouteTree>>;
  }
}
